import '../../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuestionDisplay = ({ 
  currentQuestion = null,
  questionNumber = 1,
  totalQuestions = 10,
  onTextToSpeech,
  onNextQuestion,
  isAISpeaking = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const audioRef = useRef(null);

  const mockQuestion = {
    id: 1,
    type: 'behavioral',
    category: 'Leadership',
    difficulty: 'intermediate',
    question: `Tell me about a time when you had to lead a team through a challenging project. How did you handle conflicts and ensure the project's success?`,
    hint: `Use the STAR method: Situation, Task, Action, Result. Focus on specific examples and quantifiable outcomes.`,
    timeLimit: 180,
    followUpQuestions: [
      "What would you do differently if faced with a similar situation?",
      "How did you measure the success of your leadership approach?"
    ]
  };

  const question = currentQuestion || mockQuestion;
  const audioUrl = useMemo(
    () => question?.audio_url || question?.audioUrl || '',
    [question]
  );

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPlaying(false);
  };

  useEffect(() => {
    stopAudio();
    return stopAudio;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, question?.question]);

  const handleTextToSpeech = () => {
    if (!audioUrl) {
      console.warn('No ElevenLabs/R2 audio URL found for this question.');
      return;
    }

    if (isPlaying) {
      stopAudio();
      return;
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.addEventListener('ended', () => {
      audioRef.current = null;
      setIsPlaying(false);
    }, { once: true });

    audio.addEventListener('error', () => {
      console.error('Question audio playback failed.');
      audioRef.current = null;
      setIsPlaying(false);
    }, { once: true });

    audio.play().then(() => {
      setIsPlaying(true);
      if (onTextToSpeech) onTextToSpeech(question.question, audioUrl);
    }).catch((error) => {
      console.error('Audio playback blocked or failed:', error);
      audioRef.current = null;
      setIsPlaying(false);
    });
  };

  const toggleHint = () => {
    setShowHint(!showHint);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'text-green-400 border-green-500/20 bg-green-500/10';
      case 'intermediate':
        return 'text-indigo-400 border-indigo-500/20 bg-indigo-500/10';
      case 'hard':
        return 'text-red-400 border-red-500/20 bg-red-500/10';
      default:
        return 'text-purple-400 border-purple-500/20 bg-purple-500/10';
    }
  };

  return (
    <div className="w-full p-6 space-y-4">
      {/* Question Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Icon name="MessageSquare" size={16} />
            <span>Question {questionNumber} of {totalQuestions}</span>
          </div>
          
          <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getDifficultyColor(question?.difficulty)}`}>
            <span className="capitalize">{question?.difficulty}</span>
          </div>
          
          <div className="px-2 py-1 rounded-full text-xs font-medium border border-purple-500/20 bg-purple-500/10 text-purple-400">
            {question?.category}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            iconName="HelpCircle"
            onClick={toggleHint}
            className={showHint ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-300'}
          >
            Hint
          </Button>

          <Button
            variant="ghost"
            size="sm"
            iconName={isPlaying ? "Volume2" : "VolumeX"}
            onClick={handleTextToSpeech}
            disabled={isAISpeaking || !audioUrl}
            className={isPlaying ? 'text-indigo-400 animate-pulse' : 'text-gray-400 hover:text-gray-300'}
            title={audioUrl ? 'Play ElevenLabs audio' : 'Audio is not available yet'}
          >
            {isPlaying ? 'Stop' : audioUrl ? 'Listen' : 'No Audio'}
          </Button>
        </div>
      </div>

      {/* Question Text */}
      <div className="space-y-4">
        <div className="relative">
          <p className="text-lg leading-relaxed text-white font-medium">
            {question?.question}
          </p>
          
          {/* AI Speaking Indicator */}
          {(isAISpeaking || isPlaying) && (
            <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-400 to-purple-500 rounded-full animate-pulse" />
          )}
        </div>

        {!audioUrl && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            ElevenLabs audio was not returned for this question. Check backend env values for ElevenLabs and Cloudflare R2.
          </div>
        )}

        {/* Hint Section */}
        {showHint && (
          <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4 animate-fade-in">
            <div className="flex items-start space-x-3">
              <Icon name="Lightbulb" size={20} className="text-indigo-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-indigo-400 mb-2">Helpful Tip</h4>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {question?.hint}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Question Type Badge */}
        <div className="flex items-center space-x-2">
          <Icon name="Tag" size={16} className="text-gray-400" />
          <span className="text-sm text-gray-400 capitalize">
            {question?.type?.replace('_', ' ')} Question
          </span>
          {question?.timeLimit && (
            <>
              <span className="text-gray-400">•</span>
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={16} className="text-gray-400" />
                <span className="text-sm text-gray-400">
                  {Math.floor(question?.timeLimit / 60)}:{(question?.timeLimit % 60)?.toString()?.padStart(2, '0')} suggested
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Actions */}
      <div className="md:hidden flex justify-between pt-4 border-t border-gray-700">
        <Button
          variant="outline"
          size="sm"
          iconName="HelpCircle"
          onClick={toggleHint}
          className="border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white"
        >
          {showHint ? 'Hide Hint' : 'Show Hint'}
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          iconName="ArrowRight"
          onClick={onNextQuestion}
          className="border-indigo-500/50 hover:border-indigo-400 text-indigo-400 hover:text-indigo-300"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default QuestionDisplay;
